import { type JSX, type ReactElement, useEffect, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  MapMarker,
  MapPopover,
  type MapPopoverProps,
  MapRoute,
} from "@frontend/components/organisms/MapView";
import { useLingui } from "@lingui/react/macro";

import { useAddressMap } from "../hooks/useAddressMap";
import { useRoute } from "../hooks/useRoute";
import type { RouteSummary, TravelMode } from "../types";
import { useRouteText } from "./useRouteText";

export type AddressMapPopoverProps = Pick<
  MapPopoverProps,
  "side" | "align" | "width" | "height"
> & {
  addressId: number;
  title?: string;
  trigger?: ReactElement;
  routeFrom?: string;
  mode?: TravelMode;
  onRoute?: (route: RouteSummary) => void;
};

export function AddressMapPopover({
  addressId,
  title,
  trigger,
  routeFrom,
  mode,
  onRoute,
  side = "left",
  align,
  width,
  height,
}: AddressMapPopoverProps): JSX.Element {
  const { t } = useLingui();
  const routeText = useRouteText();
  const [open, setOpen] = useState(false);

  const address = useAddressMap(addressId, open);
  const route = useRoute(routeFrom ?? null, `address:${addressId}`, {
    mode,
    enabled: open && undefined !== routeFrom,
  });

  const ready =
    address.status !== "loading" &&
    (undefined === routeFrom || route.status !== "loading");

  useEffect(() => {
    if (route.status === "ready") {
      onRoute?.(route.route);
    }
  }, [route, onRoute]);

  const footer =
    address.status === "error"
      ? t`This address could not be located.`
      : route.status === "ready"
        ? routeText(route.route)
        : route.status === "error"
          ? t`No route could be computed.`
          : undefined;

  return (
    <MapPopover
      trigger={
        trigger ?? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={title ?? t`Show on the map`}
            leftSection={<Icon name="MapPin" size="sm" />}
          />
        )
      }
      title={
        title ??
        (address.status === "ready"
          ? address.address.displayAddress
          : undefined)
      }
      footer={footer}
      ready={ready}
      side={side}
      align={align}
      width={width}
      height={height}
      open={open}
      onOpenChange={setOpen}
    >
      {address.status === "ready" && (
        <MapMarker
          position={{
            lat: address.address.latitude,
            lng: address.address.longitude,
          }}
          title={address.address.contactDisplayName ?? undefined}
          label={undefined !== routeFrom ? "B" : undefined}
          defaultOpen={undefined === routeFrom}
        >
          {address.address.displayAddress}
        </MapMarker>
      )}

      {route.status === "ready" && (
        <>
          <MapRoute path={route.route.polyline} />
          {route.route.legs[0] && (
            <MapMarker
              position={route.route.legs[0].start}
              label="A"
              tone="neutral"
            />
          )}
        </>
      )}
    </MapPopover>
  );
}
